import { AfterViewInit, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import * as L from 'leaflet';
import { NotificationService } from '@shared/services/notification.service';
import { FileManagerService } from '@core/services/files/file-manager.service';
import { UserDocumentDto } from '@core/models/usuario/UserDocument';

/**
 * Sección reutilizable del "expediente" de documentos obligatorios de un
 * colaborador: 5 archivos (RIF, cédula, CV, foto carnet, foto oficina) + tipo
 * de sangre + ubicación (estado/ciudad/dirección) + punto GPS en un mapa
 * Leaflet. La usan los flujos de `usuarios-tienda` y `administrar-tienda`.
 *
 * Uso:
 *  <app-documentos-usuario #docs [modo]="'crear'"></app-documentos-usuario>
 * y desde el padre (@ViewChild):
 *  - modo 'crear'  -> campos obligatorios; `documentosCompletos()` debe ser true.
 *  - modo 'editar' -> `setExpediente(doc)` para MOSTRAR lo ya cargado; el asesor
 *    puede reemplazar de a un archivo. `algunDocumentoTocado()` indica si hubo
 *    cambios; si los hay, debe quedar completo (`documentosCompletos()`).
 *  - `buildFormData()` arma el FormData (sólo los archivos NUEVOS) para
 *    `UsuarioService.uploadDocuments` (el backend conserva los no reenviados).
 *  - `bloodType` (get) / `setBloodType()` para el dato personal.
 */
@Component({
  selector: 'app-documentos-usuario',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './documentos-usuario.component.html',
  styleUrl: './documentos-usuario.component.scss',
})
export class DocumentosUsuarioComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() modo: 'crear' | 'editar' = 'crear';
  @Input() mapId = 'map-documentos';

  private fileManager = inject(FileManagerService);

  readonly bloodTypes = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'];
  readonly campos = ['rifFile', 'cedula', 'cv', 'carnetFoto', 'oficioFoto'] as const;

  // Mapea cada campo del formulario a las llaves {id,name} del expediente guardado.
  private readonly EXIST_KEYS: Record<string, { idKey: keyof UserDocumentDto; nameKey: keyof UserDocumentDto }> = {
    rifFile: { idKey: 'rifId', nameKey: 'rifName' },
    cedula: { idKey: 'cedulaId', nameKey: 'cedulaName' },
    cv: { idKey: 'cvId', nameKey: 'cvName' },
    carnetFoto: { idKey: 'carnetFotoId', nameKey: 'carnetFotoName' },
    oficioFoto: { idKey: 'oficioFotoId', nameKey: 'oficioFotoName' },
  };

  archivosDoc: { [campo: string]: File | undefined } = {};
  // Expediente ya guardado (para mostrar/descargar sin volver a subir).
  existentes: { [campo: string]: { id: string; name: string } | undefined } = {};
  descargando: { [campo: string]: boolean } = {};
  geocodificando = false;

  form = this.fb.group({
    frmBloodType: [''],
    frmEstado: [''],
    frmCiudad: [''],
    frmOficioAddress: ['', [Validators.maxLength(255)]],
    frmGpsLat: [null as number | null],
    frmGpsLng: [null as number | null],
  });

  private map?: L.Map;
  private marker?: L.Marker;

  constructor(private fb: FormBuilder, private notification: NotificationService) {}

  ngOnInit(): void {
    if (this.modo === 'crear') {
      this.form.controls.frmEstado.addValidators(Validators.required);
      this.form.controls.frmCiudad.addValidators(Validators.required);
      this.form.controls.frmOficioAddress.addValidators(Validators.required);
      this.form.controls.frmGpsLat.addValidators(Validators.required);
      this.form.controls.frmGpsLng.addValidators(Validators.required);
      this.form.updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initMapa(), 0);
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.map = undefined;
  }

  /**
   * Carga el expediente YA guardado: muestra los archivos existentes (con descarga),
   * y precarga ubicación, GPS y tipo de sangre. Deja el form 'pristine' para que
   * `algunDocumentoTocado()` sólo sea true si el asesor cambia algo después.
   */
  setExpediente(doc?: UserDocumentDto | null): void {
    if (!doc) return;
    this.existentes = {};
    this.campos.forEach((c) => {
      const { idKey, nameKey } = this.EXIST_KEYS[c];
      const id = doc[idKey] as string | undefined;
      const name = doc[nameKey] as string | undefined;
      if (id) this.existentes[c] = { id, name: name || 'archivo' };
    });
    this.form.patchValue({
      frmEstado: doc.estado ?? '',
      frmCiudad: doc.ciudad ?? '',
      frmOficioAddress: doc.oficioAddress ?? '',
      frmGpsLat: doc.gpsLatitude ?? null,
      frmGpsLng: doc.gpsLongitude ?? null,
    });
    this.form.markAsPristine();
    this.colocarMarcadorSiHayGps();
  }

  private initMapa(): void {
    if (this.map || !document.getElementById(this.mapId)) return;
    this.map = L.map(this.mapId).setView([8.0, -66.0], 6); // Venezuela
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(this.map);
    this.map.on('click', (e: L.LeafletMouseEvent) => this.setUbicacion(e.latlng.lat, e.latlng.lng));
    setTimeout(() => this.map?.invalidateSize(), 200);
    // Si ya se cargó un expediente con GPS antes de inicializar el mapa, dibujarlo.
    this.colocarMarcadorSiHayGps();
  }

  /** Dibuja/mueve el marcador y centra el mapa si hay coordenadas cargadas. */
  private colocarMarcadorSiHayGps(): void {
    const { frmGpsLat, frmGpsLng } = this.form.getRawValue();
    if (this.map && frmGpsLat != null && frmGpsLng != null) {
      this.dibujarMarcador(frmGpsLat, frmGpsLng);
      this.map.setView([frmGpsLat, frmGpsLng], 14);
    }
  }

  private dibujarMarcador(lat: number, lng: number): void {
    const icon = L.divIcon({
      className: 'pin-documento',
      html: '<div style="font-size:26px;line-height:26px;">📍</div>',
      iconSize: [26, 26],
      iconAnchor: [13, 26],
    });
    if (!this.marker) {
      this.marker = L.marker([lat, lng], { icon }).addTo(this.map!);
    } else {
      this.marker.setLatLng([lat, lng]);
    }
  }

  private setUbicacion(lat: number, lng: number, marcarDirty = true): void {
    this.dibujarMarcador(lat, lng);
    this.form.patchValue({
      frmGpsLat: Number(lat.toFixed(6)),
      frmGpsLng: Number(lng.toFixed(6)),
    });
    // Solo cuenta como "cambio" si fue DELIBERADO (clic en el mapa o botón
    // "Buscar"). El autogeocodificado al salir de un campo (auto=true) NO debe
    // ensuciar el form: en una edición de solo-datos no debe gatillar el bloqueo
    // ni la subida del expediente (ni pisar el GPS con una aproximación).
    if (marcarDirty) this.form.markAsDirty();
  }

  /**
   * Geocodifica Estado + Ciudad + Dirección con Nominatim (OpenStreetMap, el mismo
   * proveedor de los tiles) y coloca el punto en el mapa automáticamente.
   *
   * Usa `fetch` nativo A PROPÓSITO: el HttpClient de la app agrega el token de sesión
   * a TODA request (token.interceptor) y no debe filtrarse a un dominio externo.
   * `auto=true` (al salir de un campo) NO pisa un punto ya fijado, para "precargar"
   * sin molestar cuando el asesor ya ajustó el pin.
   */
  async buscarEnMapa(auto = false): Promise<void> {
    if (this.geocodificando) return;
    const d = this.form.getRawValue();
    const estado = (d.frmEstado || '').trim();
    const ciudad = (d.frmCiudad || '').trim();
    const direccion = (d.frmOficioAddress || '').trim();

    if (auto && d.frmGpsLat != null) return; // ya hay un punto fijado: no lo pisamos
    if (!ciudad && !estado) {
      if (!auto) this.notification.warning('Indicá al menos estado o ciudad para ubicar en el mapa.');
      return;
    }

    const query = encodeURIComponent([direccion, ciudad, estado, 'Venezuela'].filter(Boolean).join(', '));
    this.geocodificando = true;
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=ve&q=${query}`;
      const resp = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
      const data = await resp.json();
      if (Array.isArray(data) && data.length) {
        const lat = Number(data[0].lat);
        const lng = Number(data[0].lon);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          this.setUbicacion(lat, lng, !auto); // auto (blur) NO ensucia el form
          this.map?.setView([lat, lng], 15);
          if (!auto) this.notification.success('Ubicación encontrada. Ajustá el pin en el mapa si hace falta.');
        }
      } else if (!auto) {
        this.notification.warning('No se encontró la dirección. Marcá el punto manualmente en el mapa.');
      }
    } catch {
      if (!auto) this.notification.warning('No se pudo buscar la dirección. Marcá el punto manualmente.');
    } finally {
      this.geocodificando = false;
    }
  }

  onArchivoSeleccionado(event: Event, campo: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      this.notification.warning('El archivo excede el límite de 10 MB');
      input.value = '';
      return;
    }
    this.archivosDoc[campo] = file;
  }

  /** Descarga un archivo YA guardado del expediente (por su id de almacenamiento). */
  descargarExistente(campo: string): void {
    const ref = this.existentes[campo];
    if (!ref?.id || this.descargando[campo]) return;
    this.descargando[campo] = true;
    this.fileManager.downloadFile(ref.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = ref.name || 'archivo';
        a.click();
        URL.revokeObjectURL(url);
        this.descargando[campo] = false;
      },
      error: () => {
        this.descargando[campo] = false;
        this.notification.warning('No se pudo descargar el archivo.');
      },
    });
  }

  get bloodType(): string {
    return this.form.getRawValue().frmBloodType || '';
  }

  setBloodType(value?: string | null): void {
    if (value) this.form.patchValue({ frmBloodType: value });
  }

  /** ¿El campo tiene archivo, sea uno nuevo elegido o uno ya guardado? */
  tienePresente(campo: string): boolean {
    return !!this.archivosDoc[campo] || !!this.existentes[campo];
  }

  /** Todos los archivos (nuevos o existentes) + ubicación + GPS presentes. */
  documentosCompletos(): boolean {
    const archivosOk = this.campos.every((c) => this.tienePresente(c));
    const d = this.form.getRawValue();
    return archivosOk && !!d.frmEstado && !!d.frmCiudad && !!d.frmOficioAddress && d.frmGpsLat != null && d.frmGpsLng != null;
  }

  /** ¿El asesor cambió algo del expediente? (archivo nuevo o edición de ubicación/GPS). */
  algunDocumentoTocado(): boolean {
    const archivoNuevo = this.campos.some((c) => !!this.archivosDoc[c]);
    return archivoNuevo || this.form.dirty;
  }

  /** ¿Adjuntó al menos un archivo NUEVO? (no cuenta ubicación/GPS/tipo de sangre). */
  hayArchivosNuevos(): boolean {
    return this.campos.some((c) => !!this.archivosDoc[c]);
  }

  buildFormData(): FormData {
    const fd = new FormData();
    // Sólo los archivos NUEVOS; el backend conserva los que no se reenvían.
    this.campos.forEach((c) => {
      const f = this.archivosDoc[c];
      if (f) fd.append(c, f);
    });
    const d = this.form.getRawValue();
    fd.append('estado', d.frmEstado ?? '');
    fd.append('ciudad', d.frmCiudad ?? '');
    fd.append('oficioAddress', d.frmOficioAddress ?? '');
    fd.append('gpsLatitude', String(d.frmGpsLat));
    fd.append('gpsLongitude', String(d.frmGpsLng));
    return fd;
  }

  reset(): void {
    this.form.reset();
    this.archivosDoc = {};
    this.existentes = {};
    this.descargando = {};
    if (this.marker) {
      this.marker.remove();
      this.marker = undefined;
    }
  }
}
